import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import ProductCard from "@/components/ProductCard";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CATEGORY_LABELS } from "@/lib/format";
import { CATEGORY_DECK } from "@/branding";
import { Search } from "lucide-react";

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState(params.get("search") || "");
  const category = params.get("category") || "";
  const featured = params.get("featured");
  const isNew = params.get("new");
  const isBest = params.get("bestseller");

  useEffect(() => {
    (async () => {
      if (isBest) {
        try {
          const r = await api.get("/products/bestsellers", { params: { limit: 24 } });
          setProducts(r.data || []);
        } catch { setProducts([]); }
        return;
      }
      const query = { limit: 60 };
      if (category) query.category = category;
      if (search) query.search = search;
      if (featured) query.featured = true;
      try {
        const r = await api.get("/products", { params: query });
        setProducts(r.data || []);
      } catch { setProducts([]); }
    })();
  }, [category, search, featured, isNew, isBest]);

  const title = featured ? "Featured" : isNew ? "New Releases" : isBest ? "Best Sellers" : category ? (CATEGORY_LABELS[category] || category) : "All products";
  const eyebrow = featured ? "Hand-picked" : isNew ? "Fresh out of the studio" : isBest ? "Loved by customers" : "The shop";

  return (
    <div className="min-h-screen flex flex-col bg-cream" data-testid="shop-page">
      <Header />
      <CartDrawer />
      <section className="max-w-7xl mx-auto px-5 lg:px-10 pt-10 sm:pt-14 pb-6 w-full">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-gold">{eyebrow}</div>
            <h1 className="editorial font-serif-display text-4xl sm:text-5xl mt-3 text-plum-deep">{title}</h1>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-plum/50" />
              <Input
                className="pl-9 h-11 rounded-full w-full sm:w-72 bg-white border-brand"
                placeholder="Search products..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="shop-search-input"
              />
            </div>
            <Select
              value={category || "all"}
              onValueChange={(v) => {
                const next = new URLSearchParams(params);
                if (v === "all") next.delete("category"); else next.set("category", v);
                // Clear exclusive filters when category is chosen
                next.delete("new"); next.delete("bestseller"); next.delete("featured");
                setParams(next);
              }}
            >
              <SelectTrigger className="h-11 rounded-full w-full sm:w-56 bg-white border-brand" data-testid="shop-category-select">
                <SelectValue placeholder="All categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {CATEGORY_DECK.map((c) => (
                  <SelectItem key={c.slug} value={c.slug} data-testid={`cat-option-${c.slug}`}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-10 pb-24 w-full">
        {products.length === 0 ? (
          <div className="border border-dashed border-brand rounded-sm py-20 text-center text-plum/60 bg-white/60 text-sm" data-testid="shop-empty">
            No products match your filters.
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 sm:gap-x-6 gap-y-12 mt-8">
            {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
